import { ArrowRight, TrendingUp, Zap, Code, Target, BarChart3, PieChart } from 'lucide-react';
import { lazy, Suspense, useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { usePageMeta } from '@/hooks/usePageMeta';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
const GoogleReviewsWidget = lazy(() => import('@/components/GoogleReviewsWidget'));

// Lazy load components to reduce initial bundle size
const ContactFormSimplified = lazy(() => import('@/components/ContactFormSimplified'));
const FAQSection = lazy(() => import('@/components/FAQSection'));


// Lazy load sections with Intersection Observer
function LazySection({ children }: { children: React.ReactNode }) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    // Only defer on Desktop (>768px) to reduce TBT without hurting Mobile
    const isDesktop = window.innerWidth > 768;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Defer visibility update only on Desktop to reduce TBT
          if (isDesktop) {
            timeoutId = setTimeout(() => {
              setIsVisible(true);
              observer.unobserve(entry.target);
            }, 50);
          } else {
            // Immediate on Mobile
            setIsVisible(true);
            observer.unobserve(entry.target);
          }
        }
      },
      { rootMargin: '50px' }
    );
    
    if (ref.current) observer.observe(ref.current);
    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, []);
  
  return <div ref={ref}>{isVisible ? children : null}</div>;
}

/**
 * Design Philosophy: Minimalismo Corporativo
 * - Hierarquia tipográfica forte
 * - Espaço em branco agressivo
 * - Cards com hover sutil
 * - CTAs bem definidas
 * - Animações fade-in ao scroll
 */

interface ServiceCard {
  id: number;
  title: string;
  description: string;
  icon: React.ReactNode;
  href: string;
}

const services: ServiceCard[] = [
  {
    id: 1,
    title: 'SEO para Advogados',
    description: 'Otimizamos seu site para que seus futuros clientes te encontrem primeiro. Posicionamos seu escritório de advocacia nas primeiras posições do Google, aumentando sua visibilidade e atraindo pessoas que já estão buscando por assessoria jurídica, sem depender de indicações ou anúncios pagos.',
    icon: <div title="Agência de Marketing Jurídico | SEO" role="img" aria-label="Agência de Marketing Jurídico | SEO" data-seo="agencia-de-marketing-juridico-seo"><TrendingUp size={32} className="text-[#1f6f9f]" /></div>,
    href: '/servicos/seo-para-advogados',
  },
  {
    id: 2,
    title: 'Tráfego Pago para Advogados',
    description: 'Atraia clientes qualificados para o seu escritório com campanhas de tráfego pago focadas no mercado jurídico. Criamos e gerenciamos anúncios estratégicos no Google e nas redes sociais para gerar consultas reais, dentro das normas da OAB, com foco em retorno sobre investimento.',
    icon: <div title="Agência de Marketing Jurídico | Tráfego Pago" role="img" aria-label="Agência de Marketing Jurídico | Tráfego Pago" data-seo="agencia-de-marketing-juridico-trafego-pago"><Zap size={32} className="text-[#1f6f9f]" /></div>,
    href: '/servicos/trafego-pago-para-advogados',
  },
  {
    id: 3,
    title: 'Sites para Advogados',
    description: 'Criamos sites profissionais para advogados: rápidos, responsivos e feitos para transmitir autoridade e conquistar clientes. Sua presença digital com a credibilidade que sua advocacia merece.',
    icon: <div title="Agência de Marketing Jurídico | Criação de Páginas" role="img" aria-label="Agência de Marketing Jurídico | Criação de Paginas" data-seo="agencia-de-marketing-juridico-criacao-de-paginas"><Code size={32} className="text-[#1f6f9f]" /></div>,
    href: '/servicos/sites-para-advogados',
  },
  {
    id: 4,
    title: 'Landing Pages para Advogados',
    description: 'Criamos landing pages estratégicas para captar clientes na área jurídica. Design profissional, foco em conversão e alinhado às normas da OAB para transformar visitantes em consultas agendadas.',
    icon: <div title="Agência de Marketing Jurídico | Landing Pages" role="img" aria-label="Agência de Marketing Jurídico | Landing Pages" data-seo="agencia-de-marketing-juridico-criacao-landing-pages"><Target size={32} className="text-[#1f6f9f]" /></div>,
    href: '/servicos/landing-pages-para-advogados',
  },
  {
    id: 5,
    title: 'CRM para Advogados',
    description: 'Organize sua captação de clientes com um CRM feito para escritórios de advocacia. Centralizamos seus contatos, estruturamos o funil de captação (do primeiro contato ao fechamento) e eliminamos a perda de leads por falta de acompanhamento. Mais controle sobre seus casos, mais conversão de consultas em clientes.',
    icon: <div title="Agência de Marketing Jurídico | Implementação de CRM" role="img" aria-label="Agência de Marketing Jurídico | Implementação de CRM" data-seo="agencia-de-marketing-juridico-criacao-landing-pages"><BarChart3 size={32} className="text-[#1f6f9f]" /></div>,
    href: '/servicos/crm-para-advogados',
  },
  {
    id: 6,
    title: 'Marketing de Conteúdo para Advogados',
    description: 'Pare de decidir no achismo. Analisamos os dados da sua captação: origem dos leads, taxa de conversão, custo por cliente e áreas mais rentáveis, e mostramos exatamente onde investir e onde cortar. Relatórios claros, sem jargão técnico, para você tomar decisões rápidas e aumentar o retorno do seu escritório.',
    icon: <div title="Agência de Marketing Jurídico | Análise de Dados" role="img" aria-label="Agência de Marketing Jurídico | Análise de Dados" data-seo="agencia-de-marketing-juridico-criacao-landing-pages"><PieChart size={32} className="text-[#1f6f9f]" /></div>,
    href: '/servicos/marketing-de-conteudo-para-advogados',
  },
];

export default function Home() {
  useEffect(() => {
    // document.title será definido pelo hook usePageMeta abaixo
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Agência de Marketing Jurídico especializada em SEO, Tráfego Pago, Criação de Sites e Landing Pages para Advogados. Aumente seus leads e sua autoridade online.');
    }
  }, []);


  const [timeLeft, setTimeLeft] = useState<string>('48:00:00');

  // Countdown timer
  useEffect(() => {
    const startTime = localStorage.getItem('countdownStart') || new Date().getTime().toString();
    localStorage.setItem('countdownStart', startTime);
    const start = parseInt(startTime);
    const totalSeconds = 48 * 60 * 60; // 48 horas em segundos

    const interval = setInterval(() => {
      const elapsed = Math.floor((new Date().getTime() - start) / 1000);
      const remaining = Math.max(0, totalSeconds - elapsed);
      
      const hours = Math.floor(remaining / 3600);
      const minutes = Math.floor((remaining % 3600) / 60);
      const seconds = remaining % 60;
      
      setTimeLeft(`${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Meta tags ÚNICOS para esta página
  usePageMeta(
    'GO Marketing Digital | Agência de Marketing Jurídico',
    'Agência especializada em marketing jurídico digital. Aumentamos leads em 40% em 90 dias com SEO, tráfego pago e sites para escritórios de advocacia.',
    undefined,
    true // isHome
  );

  // LocalBusiness Schema JSON-LD
  useEffect(() => {
    const localBusinessSchema = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      'name': 'GO Marketing Digital',
      'description': 'Agência de Marketing Jurídico especializada em SEO, Tráfego Pago, Criação de Sites e Landing Pages para Advogados',
      'url': 'https://www.gomarketingdigital.com.br',
      'telephone': '+55 11 97132-8412',
      'email': 'contato@gomarketingdigital.com.br',
      'address': {
        '@type': 'PostalAddress',
        'addressLocality': 'São Paulo',
        'addressRegion': 'SP',
        'addressCountry': 'BR'
      },
      'sameAs': [
        'https://www.facebook.com/gomarketingdigital',
        'https://www.instagram.com/gomarketingdigital',
        'https://www.linkedin.com/company/gomarketingdigital'
      ]
    };

    const localBusinessScript = document.createElement('script');
    localBusinessScript.type = 'application/ld+json';
    localBusinessScript.textContent = JSON.stringify(localBusinessSchema);
    document.head.appendChild(localBusinessScript);
  }, []);

  // FAQ Schema JSON-LD
  useEffect(() => {
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      'mainEntity': [
        {
          '@type': 'Question',
          'name': 'Como funciona o SEO para advogados?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'Otimizamos seu site para que seus futuros clientes te encontrem primeiro nas buscas do Google. Posicionamos seu escritório nas primeiras posições, aumentando visibilidade e atraindo pessoas que já estão buscando por assessoria jurídica.'
          }
        },
        {
          '@type': 'Question',
          'name': 'Qual é o resultado médio com tráfego pago?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'Nossos clientes aumentam suas receitas em média 40% nos primeiros 6 meses com campanhas de tráfego pago estratégicas dentro das normas da OAB.'
          }
        },
        {
          '@type': 'Question',
          'name': 'Quanto tempo leva para ver resultados?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'Com tráfego pago, você vê resultados em dias. Com SEO, normalmente entre 3 a 6 meses para posições competitivas. Oferecemos uma estratégia grátis para avaliar seu caso específico.'
          }
        },
        {
          '@type': 'Question',
          'name': 'Vocês respeitam as normas da OAB?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'Sim, todas as nossas estratégias de marketing jurídico respeitam rigorosamente as normas da OAB e regulamentações profissionais.'
          }
        },
        {
          '@type': 'Question',
          'name': 'Qual é o investimento mínimo em marketing jurídico?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'O investimento varia conforme os objetivos e a área de atuação. Oferecemos planos flexíveis e uma consultoria gratuita para definir a melhor estratégia para seu escritório.'
          }
        },
        {
          '@type': 'Question',
          'name': 'Como medir o ROI (Retorno sobre Investimento) das campanhas?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'Utilizamos ferramentas de análise e relatórios detalhados para acompanhar o desempenho das campanhas, mostrando o retorno sobre cada investimento realizado.'
          }
        },
        {
          '@type': 'Question',
          'name': 'Vocês oferecem consultoria gratuita?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'Sim, oferecemos uma consultoria estratégica gratuita para entender suas necessidades e propor as melhores soluções de marketing jurídico para seu escritório.'
          }
        },
        {
          '@type': 'Question',
          'name': 'Qual a diferença entre SEO e Tráfego Pago?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'SEO foca em resultados orgânicos a longo prazo, melhorando o posicionamento natural no Google. Tráfego Pago gera visibilidade imediata através de anúncios pagos, com controle preciso sobre o público-alvo.'
          }
        }
      ]
    };

    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(faqScript);
  }, []);

  // Review Schema removed - using Google Reviews Widget instead for better validation

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />

      <main className="flex-1">
        {/* Hero Section - LCP Optimized */}
        <section
          className="relative py-8 sm:py-12 md:py-20 lg:py-32 overflow-visible min-h-auto md:min-h-[700px] bg-white"
        >
          <div className="w-full px-3 sm:px-4 md:px-6 lg:px-8 relative z-10">
            <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-start md:items-center w-full md:max-w-6xl md:mx-auto">
              {/* Text Content */}
              <div className="w-full md:flex-1 space-y-3 sm:space-y-4 md:space-y-6 lg:space-y-8 text-center md:text-left flex flex-col justify-center">
                <h1 className="text-lg sm:text-xl md:text-3xl lg:text-5xl font-bold text-[#1f6f9f] leading-snug md:leading-tight max-w-2xl" id="hero-title">
                  Agência de Marketing Jurídico
                </h1>
                <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed font-semibold mb-2">
                  Sua parceira estratégica para o sucesso no marketing jurídico digital.
                </p>
                <div className="inline-block bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs sm:text-sm font-bold">
                  ⏰ Oferta válida por {timeLeft}
                </div>
              <p className="text-xs sm:text-sm md:text-lg lg:text-xl text-gray-700 leading-relaxed font-semibold mb-2">
                Aumente Seus Leads em 40% em 90 Dias
              </p>
              <p className="text-xs sm:text-sm md:text-lg lg:text-xl text-gray-700 leading-relaxed">
                Somos uma Agência de Marketing Jurídico. Transformamos SEO, Tráfego Pago e Sites em novos clientes para o seu escritório, com estratégias pensadas para o mercado jurídico, não fórmulas genéricas.
              </p>
              <div className="flex flex-col gap-2 sm:gap-3 w-full sm:flex-row justify-center md:justify-start pt-2 sm:pt-3">
                <a
                  href="#formulario-contato"
                  className="px-3 sm:px-6 md:px-8 lg:px-10 py-2.5 sm:py-3 md:py-3.5 bg-gray-200 text-gray-800 rounded-lg font-semibold hover:bg-gray-300 transition-all duration-300 hover:shadow-xl flex items-center justify-center gap-2 text-xs sm:text-sm md:text-base h-10 sm:h-11 md:h-12"
                >
                  Agendar Consulta
                </a>
              </div>
              <div className="flex gap-4 sm:gap-6 justify-center md:justify-start text-xs sm:text-sm font-semibold text-gray-700 pt-2">
                <div>500+ Clientes</div>
                <div>40% Aumento Medio</div>
                <div>4.9/5 Satisfacao</div>
              </div>
              </div>

              {/* Image */}
              <div className="w-full md:w-auto flex justify-center md:justify-end aspect-[3/4]" style={{ contain: 'layout style paint' }}>
                <picture>
                  <source
                    media="(max-width: 767px)"
                    srcSet="/manus-storage/advogado-hero-mobile_9c58cc5a.webp"
                    type="image/webp"
                  />
                  <source
                    media="(min-width: 768px)"
                    srcSet="/manus-storage/advogado-hero-desktop_2f156f84.webp"
                    type="image/webp"
                  />
                  <img
                    src="/manus-storage/advogado-hero-mobile_9c58cc5a.webp" 
                    alt="Advogado profissional - Agencia de Marketing Juridico" 
                    className="h-full w-full object-cover max-w-xs sm:max-w-sm md:max-w-md"
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                    sizes="(max-width: 768px) 100vw, 50vw"
                    width="400"
                    height="533"
                    style={{ contain: 'layout style paint' }}
                  />
                </picture>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="py-8 sm:py-12 md:py-16 lg:py-24 bg-gray-50" id="porque-escolher">
          <div className="container mx-auto px-4">
            <div className="text-center mb-8 sm:mb-12 md:mb-16">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#1f6f9f] mb-3 sm:mb-4">
                Por que escolher nossa Agência de Marketing Jurídico?
              </h2>
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 max-w-3xl mx-auto">
                Somos a Agência de Marketing Jurídico que entende as nuances do mercado jurídico. Nossa expertise garante que seu escritório se destaque, atraia clientes qualificados e cresça de forma ética e sustentável.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300">
                <h3 className="text-xl font-semibold text-[#1f6f9f] mb-3">Especialização Jurídica</h3>
                <p className="text-gray-700">Focamos exclusivamente no setor jurídico, garantindo estratégias que respeitam as normas da OAB e geram resultados reais para advogados e escritórios de advocacia.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300">
                <h3 className="text-xl font-semibold text-[#1f6f9f] mb-3">Resultados Comprovados</h3>
                <p className="text-gray-700">Nossa Agência de Marketing Jurídico tem um histórico de sucesso, com clientes que aumentaram significativamente seus leads e faturamento através de nossas campanhas.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300">
                <h3 className="text-xl font-semibold text-[#1f6f9f] mb-3">Abordagem Personalizada</h3>
                <p className="text-gray-700">Cada estratégia é desenvolvida sob medida para as necessidades do seu escritório, garantindo que você receba um plano de marketing jurídico que realmente funcione.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="below-fold-section py-8 sm:py-12 md:py-16 lg:py-24 bg-white" id="servicos">
          <div className="container mx-auto px-4">
            <div className="text-center mb-8 sm:mb-12 md:mb-16">
              <p className="text-sm sm:text-base font-semibold text-[#1f6f9f] uppercase tracking-wide mb-2">
                Serviços
              </p>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#1f6f9f] mb-3 sm:mb-4">
                Nossa Agência de Marketing Jurídico Oferece Soluções Completas para Advogados
              </h2>
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 max-w-2xl mx-auto">
                Agência de Marketing Jurídico - Cada serviço foi desenvolvido especificamente para advogados que querem crescer de forma sustentável e ética.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {services.map((service) => (
                <Link key={service.id} to={service.href} className="p-6 sm:p-8 rounded-lg border-2 border-gray-200 hover:border-[#1f6f9f] hover:shadow-lg transition-all duration-300 min-h-[280px] flex flex-col items-center text-center cursor-pointer block">
                    <div className="mb-4 flex justify-center">{service.icon}</div>
                    <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-[#1f6f9f] mb-2 sm:mb-3 hover:text-[#0d47a1] transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed">
                      {service.description}
                    </p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Results Section */}
        <section id="resultados" className="py-8 sm:py-12 md:py-16 lg:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-8 sm:mb-12 md:mb-16">
              <p className="text-sm sm:text-base font-semibold text-[#1f6f9f] uppercase tracking-wide mb-2">
                Resultados
              </p>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#1f6f9f] mb-3 sm:mb-4">
                Empresas que Confiam em Nós
              </h2>
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 max-w-2xl mx-auto">
                Nossos clientes aumentaram suas receitas em média 40% nos primeiros 6 meses.
              </p>
              <div className="mt-4 sm:mt-6 inline-block bg-gradient-to-r from-[#1f6f9f] to-[#0d47a1] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-bold text-lg sm:text-xl md:text-2xl shadow-lg">
                💰 + de 3 Milhões em Faturamento Gerado
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
              {/* Row 1 - 4 logos */}
              <div className="w-32 sm:w-40 md:w-56 lg:w-72 h-32 sm:h-40 md:h-56 lg:h-72 flex items-center justify-center bg-transparent shadow-none border-0 rounded-none p-0">
                <img src="/manus-storage/nunes_09531c82.webp" alt="Nunes Direito à Saúde e Seguros - Cliente de Agencia de Marketing Juridico" className="results-logo w-full h-full object-contain p-0" loading="lazy" decoding="async" width="354" height="87" />
              </div>
              <div className="w-32 sm:w-40 md:w-56 lg:w-72 h-32 sm:h-40 md:h-56 lg:h-72 flex items-center justify-center bg-transparent shadow-none border-0 rounded-none p-0">
                <img src="/manus-storage/saraiva_2c08b31c_4d504c24.webp" alt="Saraiva - Cliente de Agencia de Marketing Juridico" className="results-logo w-full h-full object-contain p-0" loading="lazy" decoding="async" width="320" height="209" />
              </div>
              <div className="w-32 sm:w-40 md:w-56 lg:w-72 h-32 sm:h-40 md:h-56 lg:h-72 flex items-center justify-center bg-transparent shadow-none border-0 rounded-none p-0">
                <img src="/manus-storage/beatriz_120b5cd7_ce30d5f5.webp" alt="Dra. Beatriz Calderaro - Cliente de Agencia de Marketing Juridico" className="results-logo w-full h-full object-contain p-0" loading="lazy" decoding="async" width="320" height="261" />
              </div>
              <div className="w-32 sm:w-40 md:w-56 lg:w-72 h-32 sm:h-40 md:h-56 lg:h-72 flex items-center justify-center bg-transparent shadow-none border-0 rounded-none p-0">
                <img src="/manus-storage/tania_d4daf8e5.webp" alt="Agencia de Marketing Juridico - Geracao de Leads" className="results-logo w-full h-full object-contain p-0" loading="lazy" decoding="async" width="380" height="270" />
              </div>

              {/* Row 2 - 4 logos */}
              <div className="w-32 sm:w-40 md:w-56 lg:w-72 h-32 sm:h-40 md:h-56 lg:h-72 flex items-center justify-center bg-transparent shadow-none border-0 rounded-none p-0">
                <img src="/manus-storage/carol_bfb6a2b2.webp" alt="Agencia de Marketing Juridico - Solucoes Completas" className="results-logo w-full h-full object-contain p-0" loading="lazy" decoding="async" width="257" height="168" />
              </div>
              <div className="w-32 sm:w-40 md:w-56 lg:w-72 h-32 sm:h-40 md:h-56 lg:h-72 flex items-center justify-center bg-transparent shadow-none border-0 rounded-none p-0">
                <img src="/manus-storage/magnolia_722fc6d5.webp" alt="Agencia de Marketing Juridico - Aumento de Faturamento" className="results-logo w-full h-full object-contain p-0" loading="lazy" decoding="async" width="256" height="166" />
              </div>
              <div className="w-32 sm:w-40 md:w-56 lg:w-72 h-32 sm:h-40 md:h-56 lg:h-72 flex items-center justify-center bg-transparent shadow-none border-0 rounded-none p-0">
                <img src="/manus-storage/galvao_fb792126.webp" alt="LG Allzão Advogados - Cliente de Agencia de Marketing Juridico" className="results-logo w-full h-full object-contain p-0" loading="lazy" decoding="async" width="170" height="180" />
              </div>
              <div className="w-32 sm:w-40 md:w-56 lg:w-72 h-32 sm:h-40 md:h-56 lg:h-72 flex items-center justify-center bg-transparent shadow-none border-0 rounded-none p-0">
                <img src="/manus-storage/karpat_a01bb42c.webp" alt="Karpat Sociedade de Advogados - Cliente de Agencia de Marketing Juridico" className="results-logo w-full h-full object-contain p-0" loading="lazy" decoding="async" width="180" height="180" />
              </div>

            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="below-fold-section py-12 sm:py-16 md:py-20 lg:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12 sm:mb-16">
              <p className="text-sm sm:text-base font-semibold text-[#1f6f9f] uppercase tracking-wide mb-2">
                Depoimentos
              </p>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#1f6f9f] mb-4">
                O Que Nossos Clientes Dizem
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {/* Testimonial 1 */}
              <div className="bg-gray-50 p-6 sm:p-8 rounded-lg border border-gray-200 hover:shadow-lg transition-shadow">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#1f6f9f] to-[#0d47a1] rounded-full flex items-center justify-center text-white font-bold text-lg">
                    MC
                  </div>
                  <div>
                    <p className="font-bold text-gray-800">Marina Costa</p>
                    <p className="text-sm text-gray-600">Advogada - Direito Tributário</p>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  "Aumentei meus leads em 60% em apenas 3 meses. A estratégia foi exatamente o que meu escritório precisava. Recomendo muito!"
                </p>
                <div className="flex items-center gap-1 text-yellow-400">
                  ⭐⭐⭐⭐⭐
                </div>
              </div>

              {/* Testimonial 2 */}
              <div className="bg-gray-50 p-6 sm:p-8 rounded-lg border border-gray-200 hover:shadow-lg transition-shadow">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#1f6f9f] to-[#0d47a1] rounded-full flex items-center justify-center text-white font-bold text-lg">
                    RS
                  </div>
                  <div>
                    <p className="font-bold text-gray-800">Roberto Silva</p>
                    <p className="text-sm text-gray-600">Sócio - Direito Empresarial</p>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  "Faturamento subiu 45% no primeiro semestre. A GO Marketing Digital entende realmente o mercado jurídico."
                </p>
                <div className="flex items-center gap-1 text-yellow-400">
                  ⭐⭐⭐⭐⭐
                </div>
              </div>

              {/* Testimonial 3 */}
              <div className="bg-gray-50 p-6 sm:p-8 rounded-lg border border-gray-200 hover:shadow-lg transition-shadow">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#1f6f9f] to-[#0d47a1] rounded-full flex items-center justify-center text-white font-bold text-lg">
                    AP
                  </div>
                  <div>
                    <p className="font-bold text-gray-800">Alessandra Pereira</p>
                    <p className="text-sm text-gray-600">Advogada - Direito Família</p>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  "Melhor investimento que fiz no meu escritório. Agora tenho mais clientes do que conseguia atender antes."
                </p>
                <div className="flex items-center gap-1 text-yellow-400">
                  ⭐⭐⭐⭐⭐
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form Section - Simplified */}
        <Suspense fallback={<div style={{ minHeight: '600px' }} />}>
          <ContactFormSimplified />
        </Suspense>

{/* Google Reviews Widget */}
        <LazySection>
          <Suspense fallback={<div style={{ minHeight: "300px" }} />}>
            <GoogleReviewsWidget />
          </Suspense>
        </LazySection>

        {/* FAQ Section */}
        <LazySection>
          <Suspense fallback={<div style={{ minHeight: "500px" }} />}>
            <FAQSection />
          </Suspense>
        </LazySection>

      </main>

      <Footer />


    </div>
  );
}
